import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "core-js/features/string/replace-all";
import Classroom from "../../interfaces/Classroom";
import Assignment from "../../interfaces/Assignment";
import ClassroomAssignmentDetails from "../../interfaces/ClassroomAssignmentDetails";
import Book from "../../interfaces/Book";
import ReadleeProfile from "./ReadleeProfile";
import Page from "../../interfaces/Page";
import Submission from "../../interfaces/Submission";
import LogService from "../LogService";
import RecordedPage from "../../interfaces/RecordedPage";
import User from "../../interfaces/User";
import UserSettings from "../../interfaces/UserSettings";
import ClassroomReadingStats from "../../interfaces/ClassroomReadingStats";
import ClassCodeResult from "../../interfaces/ClassCodeResult";
import OnboardingAssignment from "../../interfaces/OnboardingAssignment";
import {
  setAdminId,
  setCloningFinished,
  setDemoAssignments,
  setDemoClassId,
  setDemoStudents,
  setDirtyWords,
  setOnboardingAssignments,
} from "../../state/readlee/readleeReducer";
import { currentUserId } from "../../App";
import Assessment from "../../interfaces/Assessment";
import AnalyticsEvent, { AnalyticsEventProps } from "../../interfaces/AnalyticsEvent";
import * as FS from "@fullstory/browser";
import AssignmentFolder from "../../interfaces/AssignmentFolder";
import axios from "axios";
import CleverClass from "../../interfaces/CleverClass";
import AnalyticsService from "../analytics/AnalyticsService";
import uuid from "react-uuid";
import { firestore as db, auth } from "./firebase";
import ComprehensionMessage from "../../interfaces/ComprehensionMessage";
import { ReadleeGoogleCourse, GoogleCourse, GoogleStudent } from "../../interfaces/Google";
import AssignmentComments from "../../interfaces/AssignmentComments";
import ComprehensionQuestion from "../../interfaces/ComprehensionQuestion";

const { FieldValue } = firebase.firestore;
// MOVE THIS OUT
const storageFilePath = (userId: string, bookId: string, pageId: string) =>
  `users/${userId}/submissions/${bookId}/recorded_pages/${pageId}`;

const UNIX_NEWLINE = "\n";
const getWordCount = (text: string) => {
  let workingText = text;
  workingText = workingText
    .replaceAll(UNIX_NEWLINE, " ")
    .replaceAll(".", " ")
    .replaceAll(",", " ")
    .replaceAll(":", " ")
    .replaceAll(";", " ")
    .replaceAll("?", " ");

  let words = workingText.split(" ");
  words = words.filter(word => word.length > 0 && word !== " ");
  return Math.min(words.length, text.length);
};

interface Student {
  // Base properties
  uid: string;
  displayName: string;
  firstName: string;
  lstName: string;
  email: string;
  userType: string;

  grade: string[];
  school: string;
  schoolId: string;

  lastLoginDay: Date;

  // Stats
  currentStreak: number;
  totalNumberOfSessions: number;
  totalReadings: number;
  totalWordsRead: number;
  wordsPerMinute: number;
}

interface GoogleProfile {
  email: string;
  given_name: string;
  family_name: string;
  name: string;
}

let demoStudents: Array<string> = [];
let demoAssignments: Array<string> = [];
let onboardingAssignments: Array<string> = [];
let demoId = "";

class FirebaseService {
  static login(userName: string, password: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      auth
        .signInWithEmailAndPassword(userName, password)
        .then(v => {
          auth.updateCurrentUser(v.user).then(() => {
            resolve();
          });
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  static loginWithCredential(credential: firebase.auth.AuthCredential): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      auth
        .signInWithCredential(credential)
        .then(v => {
          auth.updateCurrentUser(v.user).then(() => resolve());
        })
        .catch(err => {
          LogService.exception(err, `FirebaseService.loginWithCredential`, "Failed calling auth");
          reject();
        });
    });
  }

  static loginWithGoogle(): void {
    const provider = new firebase.auth.GoogleAuthProvider();
    auth.signInWithRedirect(provider);
  }

  static collectLoginData(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      auth
        .getRedirectResult()
        .then(async v => {
          if (!v.user) throw new Error("No user");
          const userDoc = await db.collection("users").where("email", "==", v.user?.email).get();
          if (userDoc.empty) {
            this.logout();
            throw new Error("No account");
          } else {
            auth.updateCurrentUser(v.user).then(() => resolve());
          }
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  static async loginWithToken(token: string): Promise<firebase.auth.UserCredential> {
    return await auth.signInWithCustomToken(token);
  }

  // sign in with google, and return google classroom data
  static getGoogleClassrooms(): Promise<Array<ReadleeGoogleCourse>> {
    const googleClassroomURL = "https://classroom.googleapis.com/v1/courses";
    const provider = new firebase.auth.GoogleAuthProvider();
    // provider.addScope("https://www.googleapis.com/auth/classroom.courses");
    provider.addScope("https://www.googleapis.com/auth/classroom.courses.readonly");
    // provider.addScope("https://www.googleapis.com/auth/classroom.rosters");
    provider.addScope("https://www.googleapis.com/auth/classroom.rosters.readonly");
    provider.addScope("https://www.googleapis.com/auth/classroom.profile.emails");
    return new Promise<Array<ReadleeGoogleCourse>>((resolve, reject) => {
      auth
        .signInWithPopup(provider)
        .then(v => {
          const credential = v.credential as firebase.auth.OAuthCredential;
          const token = credential.accessToken;
          const output: ReadleeGoogleCourse[] = [];
          fetch(googleClassroomURL + "?pageSize=200&access_token=" + token, {
            method: "GET",
          }).then(async response => {
            const courses = ((await response.json()) as { courses: GoogleCourse[] }).courses;
            for (const course of courses) {
              const result = await fetch(
                googleClassroomURL + "/" + course.id + "/students?pageSize=200&access_token=" + token,
                {
                  method: "GET",
                },
              );
              output.push({
                id: course.id,
                name: course.name,
                students: ((await result.json()) as { students: GoogleStudent[] }).students,
              });
            }
            resolve(output);
          });
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  static async addAssignmentToClass(assignmentId: string, classId: string) {
    try {
      const batch = db.batch();

      const classRef = db.collection("classes").doc(classId);
      const classInfo = (await classRef.get()).data();
      const classData = classInfo as {
        classCode: string;
        className: string;
        id: string;
        teacherEmail: string;
        teacherName: string;
        students: Array<string> | undefined;
      };

      const classAssignmentRef = db.collection("classes").doc(classId).collection("assignments").doc(assignmentId);

      batch.update(classRef, {
        assignments: firebase.firestore.FieldValue.arrayUnion(classAssignmentRef.id),
      });

      batch.set(
        classAssignmentRef,
        {
          id: classAssignmentRef.id,
          classData: {
            classCode: classData.classCode,
            className: classData.className,
            id: classData.id,
            teacherEmail: classData.teacherEmail,
            teacherName: classData.teacherName,
          },
          students: classData.students ?? {},
        },
        { merge: true },
      );

      const rootFolder = await db
        .collection("assignment_folders")
        .where("parentId", "==", null)
        .where("type", "==", classId)
        .limit(1)
        .get();

      if (!rootFolder.empty) {
        const doc = rootFolder.docs[0];
        if (doc) {
          const rootFolderRef = db.collection("assignment_folders").doc(doc.id);
          batch.update(rootFolderRef, {
            childAssignments: firebase.firestore.FieldValue.arrayUnion(assignmentId),
          });
        }
      }

      await batch.commit();
    } catch (error) {
      LogService.exception(
        error,
        `FirebaseService.addAssignmentToClass`,
        `Failed to add assignment to class ${classId}`,
      );
    }
  }

  static async deleteAssignmentFromClass(assignmentId: string, classId: string) {
    try {
      const batch = db.batch();

      const classRef = db.collection("classes").doc(classId);
      const classAssignmentRef = db.collection("classes").doc(classId).collection("assignments").doc(assignmentId);

      batch.update(classRef, {
        assignments: firebase.firestore.FieldValue.arrayRemove(classAssignmentRef.id),
      });
      batch.delete(classAssignmentRef);

      const rootFolder = await db
        .collection("assignment_folders")
        .where("parentId", "==", null)
        .where("type", "==", classId)
        .limit(1)
        .get();

      if (!rootFolder.empty) {
        const doc = rootFolder.docs[0];
        if (doc) {
          const rootFolderRef = db.collection("assignment_folders").doc(doc.id);
          batch.delete(rootFolderRef);
        }
      }

      await batch.commit();
    } catch (error) {
      LogService.exception(
        error,
        `FirebaseService.deleteAssignmentFromClass`,
        `Failed to delete assignment from class ${classId}`,
      );
    }
  }

  static async checkEmailExists(email: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      auth
        .signInWithEmailAndPassword(email, "_")
        .then(() => {
          resolve("exists");
        })
        .catch(r => {
          reject(r);
        });
    });
  }

  static signUpWithGoogle(): void {
    auth.signInWithRedirect(new firebase.auth.GoogleAuthProvider());
  }

  static async finishSignUpWithGoogle(): Promise<{ newUser: boolean; user: firebase.auth.UserCredential }> {
    const queryParams = new URLSearchParams(window.location.search);
    const userType = queryParams.get("userType") || "";
    const classCode = queryParams.get("classCode") || "";

    const res = await auth.getRedirectResult();
    if (!res.user) throw new Error("No user");
    const profile = res.additionalUserInfo?.profile as GoogleProfile | undefined;
    const userDoc = await db.collection("users").where("email", "==", profile?.email).get();

    if (userDoc.empty && profile) {
      const ref = db.collection("users").doc(res.user?.uid);

      let userProps = {
        firstName: profile.given_name,
        lastName: profile.family_name,
        displayName: profile.given_name,
        email: profile.email.toLowerCase(),
      };

      if (process.env.REACT_APP_EMULATE) {
        const [firstName = "", lastName = ""] = profile.name.split(" ");
        userProps = {
          firstName,
          lastName,
          displayName: firstName,
          email: profile.email.toLowerCase(),
        };
      }

      await ref.set({
        ...userProps,
        uid: ref.id,
        birthday: {
          day: 1,
          month: "January",
          year: 2000,
        },
        grade: [],
        subject: [],
        userType,
        school: "",
        timeCreated: Date.now(),
      });

      await this.addPreregisteredStudent(ref.id, profile.email.toLowerCase());
      if (classCode.length > 0) {
        await FirebaseService.addUserToClass(classCode, ref.id);
      }

      if (userType === "teacher") {
        this.createRootFolders(ref.id, []);
        this.cloneDemoClass(userProps.displayName, profile.email.toLowerCase(), ref.id);
      }

      return { newUser: true, user: res };
    } else {
      return { newUser: false, user: res };
    }
  }

  static async createClass(
    className: string,
    teacherName: string,
    teacherEmail: string,
    teacherId: string,
    googleId: string | null,
    students: Array<string>,
  ): Promise<string | undefined> {
    try {
      const chars = "AaBbCcDdEeFfGgHhJjKkLMmNnoPpQqRrSsTtUuVvWwXxYyZz34679";
      let classCode = "";
      const classCodeLength = 6;

      do {
        classCode = "";
        for (let i = 0; i < classCodeLength; i++) {
          classCode += chars.charAt(Math.random() * chars.length);
        }
      } while (await this.checkClassCodeExists(classCode));

      const classroomRef = db.collection("classes").doc();
      await classroomRef.set(
        {
          className: className,
          googleCourseId: googleId,
          id: classroomRef.id,
          classCode: classCode,
          teacherEmail: teacherEmail,
          teacherName: teacherName,
          teacher: {
            displayName: teacherName,
            email: teacherEmail,
            uid: teacherId,
          },
        },
        { merge: true },
      );
      await this.preRegisterStudents(students, classroomRef.id);
      await this.createNewAssignmentFolder(null, className, classroomRef.id, teacherId);
      return classroomRef.id;
    } catch (err) {
      LogService.exception(err, `FirebaseService.createClass`, "Failed to create class");
    }
    return;
  }

  static async checkClassCodeExists(code: string): Promise<boolean> {
    return (await db.collection("classes").where("classCode", "==", code).get()).size > 0;
  }

  static async preRegisterStudents(studentEmails: Array<string>, classId: string): Promise<void> {
    try {
      for (let i = 0; i < studentEmails.length; i++) {
        const currentStudent = await db.collection("users").where("email", "==", studentEmails[i]).get();
        if (currentStudent.empty) {
          const pre = await db.collection("class_pre_registrations").doc(studentEmails[i]).get();
          if (pre.exists) {
            await db
              .collection("class_pre_registrations")
              .doc(studentEmails[i])
              .update({ classes: firebase.firestore.FieldValue.arrayUnion(classId) });
          } else {
            await db
              .collection("class_pre_registrations")
              .doc(studentEmails[i])
              .set({
                id: studentEmails[i],
                classes: [classId],
              });
          }
          await db
            .collection("classes")
            .doc(classId)
            .update({ preRegisteredStudents: firebase.firestore.FieldValue.arrayUnion(studentEmails[i]) });
        } else {
          const existingStudent = currentStudent.docs[0];
          if (existingStudent) {
            await this.manageStudentOnClass(existingStudent.id, classId, true);
          }
        }
      }
    } catch (err) {
      LogService.exception(err, `FirebaseService.preRegisterStudents`, `Failed to add student to class ${classId}`);
    }
  }

  // Create assignment folder at parentId
  static async createNewAssignmentFolder(parentId: string | null, name: string, type: string, userId: string) {
    try {
      const ref = db.collection("assignment_folders").doc();
      const today = new Date();

      const timestamp = today.toISOString();

      await ref.set({
        id: ref.id,
        name: name,
        ownerId: userId,
        parentId: parentId,
        childAssignments: [],
        childFolders: [],
        // isStarred: false,
        isActive: true,
        dateCreated: timestamp,
        lastOpened: timestamp,
        lastModified: timestamp,
        type: type,
        color: { alpha: 255, red: 0, blue: 0, green: 0 },
      });

      if (parentId != null)
        await db
          .collection("assignment_folders")
          .doc(parentId)
          .update({
            childFolders: firebase.firestore.FieldValue.arrayUnion(ref.id),
            lastModified: today,
          });
    } catch (err) {
      LogService.exception(
        err,
        `FirebaseService.createNewAssignmentFolder`,
        `Failed to create class assignment root folder`,
      );
    }
  }

  static async createAccount(
    email: string,
    password: string,
    userType: string,
    school: string,
    firstName: string,
    lastName: string,
    classCode = "",
  ): Promise<void> {
    const auth = firebase.auth();
    const db = firebase.firestore();

    const user = await auth.createUserWithEmailAndPassword(email, password);
    const userId = user.user?.uid;
    const ref = db.collection("users").doc(userId);
    await ref.set({
      uid: ref.id,
      email: email,
      userType: userType,
      grade: [],
      subject: [],
      school: school,
      firstName: firstName,
      lastName: lastName,
      displayName: firstName,
      onboardingStatus: "not started",
      timeCreated: Date.now(),
      lastLoginDay: new Date().toISOString(),
    });
    await this.addPreregisteredStudent(ref.id, email);
    if (userType === "teacher") {
      this.createRootFolders(ref.id, []);
      this.cloneDemoClass(firstName, email, ref.id);
    }
    if (classCode.length > 0) {
      await FirebaseService.addUserToClass(classCode, ref.id);
    }

    const savedUser = (await (await db.collection("users").doc(userId).get()).data()) as User;
    AnalyticsService.logUserCreated(savedUser);
  }

  static async addUserToClass(classCode: string, uid: string) {
    try {
      if (!classCode || !uid) throw Error("Invalid Class Code or UID!");

      const result = await FirebaseService.checkClassCode(classCode);
      if (!result.exists) throw Error("Class Code Invalid!");

      const alreadyInClass = await FirebaseService.checkStudentOnClass(uid, result.id);
      if (alreadyInClass) return;

      await FirebaseService.manageStudentOnClass(uid, result.id, true);
    } catch (err) {
      LogService.exception(err, `FirebaseService.addUserToClass`, `Failed to add student. ${uid} to ${classCode}`);
    }
  }

  // Create demo class for new teacher
  static async cloneDemoClass(teacherName: string, teacherEmail: string, teacherId: string) {
    setCloningFinished(false);

    // Create classroom with demo students
    await this.createClass("Sample Class", teacherName, teacherEmail, teacherId, null, demoStudents).then(async id => {
      // Add demo assignments to class
      for (const assignment of demoAssignments) {
        await this.addAssignmentToClass(assignment, id as string);
      }

      const batch = db.batch();
      // Clone demo class reading stats for each demo student
      for (let j = 0; j < demoStudents.length; j++) {
        const userId = (await db.collection("users").where("email", "==", demoStudents[j]).get()).docs?.[0]?.id;

        const originalStats = (
          await db.collection("users").doc(userId).collection("classroomReadingStats").doc(demoId).get()
        ).data() as ClassroomReadingStats;

        if (originalStats)
          batch.set(db.collection("users").doc(userId).collection("classroomReadingStats").doc(id), {
            accuracy: originalStats.accuracy,
            classID: id,
            completionPercentage: originalStats.completionPercentage,
            studentID: originalStats.studentID,
            totalReadings: originalStats.totalReadings,
            totalTimeRead: originalStats.totalTimeRead,
            totalWordsRead: originalStats.totalWordsRead,
            uniqueWordsRead: originalStats.uniqueWordsRead,
            wordsPerMin: originalStats.wordsPerMin,
          });
      }

      await batch.commit();
      setCloningFinished(true);
    });
  }

  static async setupNewTeacher(
    userId: string,
    schoolName: string,
    grades: string[],
    subjects: string[],
    ellPercentage: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .update({
          school: schoolName,
          grade: grades,
          subject: subjects,
          sendOnBoardingEmail: true,
          sharedAssignments: onboardingAssignments,
          ellPercentage: ellPercentage,
        })
        .then(() => resolve())
        .catch(() => reject());
    });
  }

  static async addPreregisteredStudent(userId: string, email: string) {
    const classDoc = await db.collection("class_pre_registrations").doc(email).get();
    if (classDoc.exists) {
      const preRegisteredClasses = classDoc.data() as { classes: string[] };
      if (preRegisteredClasses.classes)
        for (const preRegisteredClass of preRegisteredClasses.classes) {
          await this.manageStudentOnClass(userId, preRegisteredClass, true);
          await db
            .collection("classes")
            .doc(preRegisteredClass)
            .update({ preRegisteredStudents: firebase.firestore.FieldValue.arrayRemove(email) });
        }
    }
  }

  static watchStudent(userId: string, callback: (student: Student) => Promise<void>): () => void {
    return db
      .collection("users")
      .doc(userId)
      .onSnapshot(
        snapshot => {
          const data = snapshot.data() as Student;
          callback(data).catch(err => {
            LogService.exception(err, `FirebaseService.watchStudent`, `Failed to process student. ${userId}`);
          });
        },
        err => {
          LogService.exception(err, `FirebaseService.watchStudent`, `Failed to watch student. ${userId}`);
        },
      );
  }

  static watchStudentClassrooms(userId: string, callback: (classrooms: Classroom[]) => Promise<void>): () => void {
    return db
      .collection("classes")
      .where(`students.${userId}`, "==", true)
      .onSnapshot(
        snapshot => {
          const classrooms = snapshot.docs.map(doc => doc.data() as Classroom);
          callback(classrooms).catch(err => {
            LogService.exception(
              err,
              `FirebaseService.watchStudentClassrooms`,
              `Failed to process classrooms. ${userId}`,
            );
          });
        },
        err => {
          LogService.exception(err, `FirebaseService.watchStudentClassrooms`, `Failed to watch classrooms. ${userId}`);
        },
      );
  }

  static watchClassroomAssignments(
    classroomId: string,
    callback: (classroomAssignmentDetails: ClassroomAssignmentDetails[]) => Promise<void>,
  ): () => void {
    return db
      .collection("classes")
      .doc(classroomId)
      .collection("assignments")
      .onSnapshot(
        snapshot => {
          const classroomAssignmentDetails = snapshot.docs.map(doc => doc.data() as ClassroomAssignmentDetails);
          callback(classroomAssignmentDetails).catch(err => {
            LogService.exception(
              err,
              `FirebaseService.watchClassroomAssignments`,
              `Failed to process classroom assignments. ${classroomId}`,
            );
          });
        },
        err => {
          LogService.exception(
            err,
            `FirebaseService.watchClassroomAssignments`,
            `Failed to watch classroom assignments. ${classroomId}`,
          );
        },
      );
  }

  static watchTeacherAssignmentFolder(
    teacherId: string,
    folderType: "Trash" | "Star",
    callback: (assignmentFolders: AssignmentFolder[]) => Promise<void>,
  ): () => void {
    return db
      .collection("assignment_folders")
      .where("ownerId", "==", teacherId)
      .where("type", "==", folderType)
      .onSnapshot(
        snapshot => {
          const assignmentFolders = snapshot.docs.map(doc => doc.data() as AssignmentFolder) as AssignmentFolder[];
          callback(assignmentFolders).catch(err => {
            LogService.exception(
              err,
              `FirebaseService.watchTeacherAssignmentFolder`,
              `Failed to process teacher ${folderType} assignment folder. ${teacherId}.`,
            );
          });
        },
        err => {
          LogService.exception(
            err,
            `FirebaseService.watchTeacherAssignmentFolder`,
            `Failed to watch teacher ${folderType} assignment folder. ${teacherId}`,
          );
        },
      );
  }

  static watchStudentRecordedPages(
    userId: string,
    bookId: string,
    callback: (recordedPages: RecordedPage[]) => Promise<void>,
  ): () => void {
    return db
      .collection("users")
      .doc(userId)
      .collection("submissions")
      .doc(bookId)
      .collection("recorded_pages")
      .onSnapshot(
        snapshot => {
          const recordedPages = snapshot.docs.map(doc => doc.data() as RecordedPage);
          callback(recordedPages).catch(error => {
            LogService.exception(
              error,
              `FirebaseService.watchStudentRecordedPages`,
              `watchStudentRecordedPages: Failed to process classrooms. ${userId}`,
            );
          });
        },
        error => {
          LogService.exception(
            error,
            `FirebaseService.watchStudentRecordedPages`,
            `watchStudentClassrooms: Failed to watch classrooms. ${userId}`,
          );
        },
      );
  }

  static log(
    type: string,
    func: string,
    message: string,
    stack: string,
    level: number,
    exception: string,
    userId: string,
    userEmail: string,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const docRef = db.collection("logs").doc();
      const timeStamp = Date.now();
      const time = new Date().toLocaleString();
      const timeEastern = new Date().toLocaleString("en-US", { timeZone: "America/New_York" });
      const timeWestern = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
      FS.log("error", `Error ${docRef.id} logged at ${timeStamp} for ${userId}`);

      docRef
        .set({
          id: docRef.id,
          type: type ?? "",
          function: func ?? "",
          message: message ?? "",
          callStack: stack ?? "",
          exception: exception ?? "",
          level: level ?? "",
          timeStamp: timeStamp,
          userId: userId,
          userEmail: userEmail,
          time: time,
          timeEastern: timeEastern,
          timeWestern: timeWestern,
        })
        .then(() => {
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  }

  static async logAnalyticsEvent(eventParams: AnalyticsEventProps): Promise<AnalyticsEvent> {
    const db = firebase.firestore();

    const ref = db.collection("analytics_events").doc();
    await ref.set(eventParams);
    return (await ref.get()).data() as AnalyticsEvent;
  }

  static getUserType(userId: string): Promise<string | undefined> {
    return new Promise<string | undefined>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .get()
        .then(snap => {
          const data = snap.data() as ReadleeProfile;
          resolve(data?.userType);
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  static getUser(userId: string): Promise<ReadleeProfile> {
    return new Promise<ReadleeProfile>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .get()
        .then(snap => {
          const data = snap.data() as ReadleeProfile;
          resolve(data);
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  static async getUsers(): Promise<ReadleeProfile[]> {
    return (await db.collection("users").get()).docs.map(doc => doc.data() as ReadleeProfile);
  }

  static watchUserClassroomReadingStats(
    userId: string,
    classroomId: string,
    callback: (classroomReadingStats: ClassroomReadingStats) => void,
  ) {
    return db
      .collection("users")
      .doc(userId)
      .collection("classroomReadingStats")
      .doc(classroomId)
      .onSnapshot(
        snap => {
          const data = snap.data() as ClassroomReadingStats;
          callback(data);
        },
        error => {
          LogService.exception(
            error,
            `FirebaseService.watchUserClassroomReadingStats`,
            `User: ${userId} Classroom: ${classroomId}`,
          );
        },
      );
  }

  static watchUser(userId: string | undefined, callback: (userData: ReadleeProfile) => void) {
    return db
      .collection("users")
      .doc(userId)
      .onSnapshot(
        snap => {
          const data = snap.data() as ReadleeProfile;
          callback(data);
        },
        error => {
          LogService.exception(error, `FirebaseService.watchUser`, `User: ${userId}`);
        },
      );
  }

  static watchUserSettings(userId: string | undefined, callback: (userData: UserSettings) => void) {
    return db
      .collection("user_settings")
      .doc(userId)
      .onSnapshot(
        snap => {
          const data = snap.data() as UserSettings;
          callback(data);
        },
        error => {
          LogService.exception(error, `FirebaseService.watchUserSettings`, `User: ${userId}`);
        },
      );
  }

  static watchTeacherClasses(userId: string, callback: (classrooms: Classroom[]) => void) {
    return db
      .collection("classes")
      .where("teacher.uid", "==", userId)
      .onSnapshot(
        snap => {
          const data = snap.docs.map(doc => doc.data() as Classroom) as Classroom[];
          callback(data);
        },
        error => {
          LogService.exception(error, `FirebaseService.watchTeacherClasses`, `User: ${userId}`);
        },
      );
  }

  static watchUserSharedAssignments(userId: string, callback: (sharedAssignments: string[]) => Promise<void>) {
    return db
      .collection("users")
      .doc(userId)
      .onSnapshot(
        snap => {
          const data = snap.data()?.sharedAssignments ?? [];
          callback(data).catch(err => {
            LogService.exception(
              err,
              `FirebaseService.watchUserSharedAssignments`,
              `Failed to process shared assignments on teacher, ${userId}.`,
            );
          });
        },
        error => {
          LogService.exception(error, `FirebaseService.watchUserSharedAssignments`, `User: ${userId}.`);
        },
      );
  }

  // Watch all assignments the teacher has created
  static watchTeacherAssignments(userId: string, callback: (assignments: Assignment[]) => Promise<void>) {
    return (
      db
        .collection("assignments")
        .where("parentId", "==", userId)
        // TODO There is a lag between when the db changes and when this snapshot registers the update
        .onSnapshot(
          snap => {
            const data = snap.docs.map(doc => doc.data() as Assignment) as Assignment[];
            callback(data).catch(error => {
              LogService.exception(
                error,
                `FirebaseService.watchTeacherAssignments`,
                `Failed to process assignments for teacher, ${userId}.`,
              );
            });
          },
          error => {
            LogService.exception(error, `FirebaseService.watchTeacherAssignments`, `User: ${userId}`);
          },
        )
    );
  }

  static watchAssignment(
    assignmentId: string,
    callback: (assignmentId: string, assignment: Assignment) => Promise<void>,
  ): () => void {
    // TODO: Create 10+ item watcher
    return db
      .collection("assignments")
      .doc(assignmentId)
      .onSnapshot(
        snapshot => {
          const data = snapshot.data() as Assignment;
          callback(snapshot.id, data).catch(err => {
            LogService.exception(
              err,
              `FirebaseService.watchAssignment`,
              `Failed to process assignment. ${assignmentId}`,
            );
          });
        },
        err => {
          LogService.exception(err, `FirebaseService.watchAssignment`, `Failed to watch assignment. ${assignmentId}`);
        },
      );
  }

  static async getAssignment(assignmentId: string): Promise<Assignment> {
    return (await db.collection("assignments").doc(assignmentId).get()).data() as Assignment;
  }

  static async getClass(classId: string): Promise<Classroom> {
    return (await db.collection("classes").doc(classId).get()).data() as Classroom;
  }

  static async getClasses(): Promise<Classroom[]> {
    return (await db.collection("classes").get()).docs.map(doc => doc.data() as Classroom);
  }

  static async getBook(bookId: string): Promise<Book> {
    return (await db.collection("library").doc(bookId).get()).data() as Book;
  }

  static async getPages(bookId: string): Promise<Page[]> {
    return (await db.collection("library").doc(bookId).collection("pages").get()).docs.map(doc => doc.data() as Page);
  }

  static async getPage(bookId: string, pageId: string): Promise<Page> {
    return (await db.collection("library").doc(bookId).collection("pages").doc(pageId).get()).data() as Page;
  }

  static async getSubmission(userId: string, assignmentId: string): Promise<Submission> {
    return (
      await db.collection("users").doc(userId).collection("submissions").doc(assignmentId).get()
    ).data() as Submission;
  }

  static async getSubmissions(userId: string): Promise<Submission[]> {
    return (await db.collection("users").doc(userId).collection("submissions").get()).docs.map(
      doc => doc.data() as Submission,
    );
  }

  static async getUserClassroomReadingStats(userId: string, classId: string): Promise<ClassroomReadingStats> {
    return (
      await db.collection("users").doc(userId).collection("classroomReadingStats").doc(classId).get()
    ).data() as ClassroomReadingStats;
  }

  static getUserComment = async (userId: string, commentId: string): Promise<ComprehensionMessage> =>
    (await db
      .collection("comprehension_messages")
      .doc(`${commentId}_${userId}`)
      .get()
      .then(snap => snap.data())) as ComprehensionMessage;

  static getStudents = async (userIds: Array<string>): Promise<Array<ReadleeProfile>> =>
    (await db.collection("users").where("uid", "in", userIds).get()).docs.map(d => d.data() as ReadleeProfile);

  static watchSubmission(
    userId: string,
    bookId: string,
    callback: (submission: Submission) => Promise<void>,
  ): () => void {
    return db
      .collection("users")
      .doc(userId)
      .collection("submissions")
      .doc(bookId)
      .onSnapshot(snapshot => {
        const data = snapshot.data() as Submission;
        callback(data).catch(e => {
          LogService.exception(e, `FirebaseService.watchSubmission`, "Failed to process submission");
        });
      });
  }

  static async checkClassCode(code: string): Promise<ClassCodeResult> {
    const defaultClassCodeResult: ClassCodeResult = { id: "", exists: false, className: "" };
    const result = await db.collection("class_codes").where("classCode", "==", code).limit(1).get();
    const doc = result.docs[0];
    if (doc) {
      const { className, id } = doc.data() as Classroom;
      return {
        ...defaultClassCodeResult,
        exists: true,
        id: id,
        ...(className ? { className } : {}),
      };
    }

    return { ...defaultClassCodeResult };
  }

  //write function for joining class
  static async manageStudentOnClass(userId: string, classId: string, assign: boolean): Promise<void> {
    try {
      const result = await db
        .collection("classes")
        .doc(classId)
        .set({ students: { [`${userId}`]: assign } }, { merge: true });

      const classRef = await db.collection("classes").doc(classId).get();
      const classroom = classRef.data() as Classroom;
      if (classroom.grade) {
        FirebaseService.updateReadleeProfile(userId, "grade", classroom.grade);
      }

      if (assign) {
        await FirebaseService.addAllAssignmentsFromClassToStudent(userId, classId);
      } else {
        await FirebaseService.removeAllAssignmentsFromClassFromStudent(userId, classId);
      }
      return result;
    } catch (err) {
      LogService.exception(
        err,
        `FirebaseService.manageStudentOnClass`,
        `Failed to ${assign ? "add" : "remove"} student, ${userId}, to all assignments on class, ${classId}.`,
      );
    }
  }

  static async checkStudentOnClass(userId: string, classId: string): Promise<boolean> {
    //problem is in this line
    const classData = await db.collection("classes").doc(classId).get();
    const studentResult = classData.data() as Classroom;
    if (studentResult.students) {
      const studentsKey = Object.keys(studentResult.students);
      const studentsValue = Object.values(studentResult.students);
      for (let i = 0; i < studentsKey.length; i++) {
        if (studentsKey[i] === userId && studentsValue[i]) {
          return true;
        }
      }
    }
    return false;
  }

  static async manageStudentOnAssignment(
    studentId: string,
    classId: string,
    assignmentId: string,
    assign: boolean,
  ): Promise<void> {
    return await db
      .collection("classes")
      .doc(classId)
      .collection("assignments")
      .doc(assignmentId)
      .set({ students: { [`${studentId}`]: assign } }, { merge: true });
  }

  static async addAllAssignmentsFromClassToStudent(userId: string, classId: string): Promise<void> {
    try {
      const classData: Classroom = await FirebaseService.getClass(classId);

      const batch = db.batch();

      classData.assignments?.forEach(assignmentId => {
        batch.set(
          db.collection("classes").doc(classId).collection("assignments").doc(assignmentId),
          { students: { [`${userId}`]: true } },
          { merge: true },
        );
      });

      await batch.commit();
      return;
    } catch (err) {
      LogService.exception(
        err,
        `FirebaseService.addAllAssignmentsFromClassToStudent`,
        `Failed to add student, ${userId}, to all assignments in class, ${classId}.`,
      );
    }
  }

  static async removeAllAssignmentsFromClassFromStudent(userId: string, classId: string): Promise<void> {
    try {
      const classData: Classroom = await FirebaseService.getClass(classId);

      const batch = db.batch();

      classData.assignments?.forEach(assignmentId => {
        batch.set(
          db.collection("classes").doc(classId).collection("assignments").doc(assignmentId),
          { students: { [`${userId}`]: false } },
          { merge: true },
        );
      });

      await batch.commit();
      return;
    } catch (err) {
      LogService.exception(
        err,
        `FirebaseService.removeAllAssignmentsFromClassFromStudent`,
        `Failed to update student, ${userId}, to false on all assignments in class, ${classId}.`,
      );
    }
  }

  static async logout(): Promise<void> {
    return await auth.signOut();
  }

  static async updateUserSettings(
    userId: string,
    key: string,
    value: string | number | number[] | boolean | undefined,
  ): Promise<void> {
    // TODO: Log errors
    return await db
      .collection("user_settings")
      .doc(userId)
      .set({ [key]: value }, { merge: true });
  }

  static async updateClassInfo(classId: string, newClassName: string, newGrade: string): Promise<void> {
    const classRef = await db.collection("classes").doc(classId).get();
    const classroom = classRef.data() as Classroom;
    const students = classroom.students;

    students &&
      Object.keys(students).forEach(id => {
        FirebaseService.updateReadleeProfile(id, "grade", newGrade);
      });

    return await db
      .collection("classes")
      .doc(classId)
      .set({ className: newClassName, grade: newGrade }, { merge: true });
  }

  static async updateReadleeProfile(
    userId: string,
    key: string,
    value: string | number | boolean | string[],
  ): Promise<void> {
    // TODO: Log errors

    return await db
      .collection("users")
      .doc(userId)
      .set({ [key]: value }, { merge: true });
  }

  static async turnInSubmission(userId: string, bookId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(bookId)
        .set({ turnedIn: true }, { merge: true })
        .then(() => resolve())
        .catch(ex => {
          LogService.exception(ex, `FirebaseService.turnInSubmission`, "Failed on set");
          reject();
        });
    });
  }

  static async recordedPageComplete(userId: string, bookId: string, pageId: string): Promise<void> {
    // TODO: Log errors
    return await db
      .collection("users")
      .doc(userId)
      .collection("submissions")
      .doc(bookId)
      .collection("recorded_pages")
      .doc(pageId)
      .set({ completed: true }, { merge: true });
  }

  static async recordedPageIncomplete(userId: string, bookId: string, pageId: string): Promise<void> {
    return await db
      .collection("users")
      .doc(userId)
      .collection("submissions")
      .doc(bookId)
      .collection("recorded_pages")
      .doc(pageId)
      .set({ completed: false }, { merge: true });
  }

  static async initializeSubmissionPage(
    userId: string,
    bookId: string,
    pageId: string,
    classId: string,
    content: string,
  ): Promise<string> {
    const batch = db.batch();
    const filePath = storageFilePath(userId, bookId, pageId);
    const wordCount = getWordCount(content);

    return new Promise<string>((resolve, reject) => {
      batch.set(
        db.collection("users").doc(userId).collection("submissions").doc(bookId),
        { classId: classId, readingStatus: "notStarted" },
        { merge: true },
      );

      batch.set(
        db
          .collection("users")
          .doc(userId)
          .collection("submissions")
          .doc(bookId)
          .collection("recorded_pages")
          .doc(pageId),
        {
          contentType: "audio/wav",
          language: "en-US", //[TODO] add accent selection.
          uid: userId,
          storageFolderPath: filePath,
          totalWords: wordCount,
          sampleRate: 48000,
          encoding: "LINEAR16",
          wordsRead: 0,
          bookId: bookId,
          pageId: pageId,
          section: 0,
          listenedToIntro: true,
        },
        { merge: true },
      );

      batch
        .commit()
        .then(() => resolve(filePath))
        .catch(ex => {
          LogService.exception(ex, `FirebaseService.initializeSubmissionPage`, "Failed executing batch");
          reject();
        });
    });
  }

  static async initializeRecordingPage(
    userId: string,
    bookId: string,
    pageId: string,
    sectionNumber: number,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(bookId)
        .collection("recorded_pages")
        .doc(pageId)
        .set(
          {
            section: sectionNumber + 1,
            transcripts: {
              [`s${sectionNumber}`]: { waitingForTranscription: true, uploading: true },
            },
            timeSubmitted: new Date().getTime(),
          },
          {
            merge: true,
          },
        )
        .then(() => resolve())
        .catch(ex => {
          LogService.exception(ex, `FirebaseService.initializeRecordingPage`, "Failed on set");
          reject();
        });
    });
  }

  static async deleteRecordingPage(userId: string, bookId: string, pageId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(bookId)
        .collection("recorded_pages")
        .doc(pageId)
        .delete()
        .then(() => resolve())
        .catch(ex => {
          LogService.exception(ex, `FirebaseService.deleteRecordingPage`, "Failed on delete");
          reject();
        });
    });
  }

  static async submitRecordingPage(userId: string, bookId: string, pageId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(bookId)
        .collection("recorded_pages")
        .doc(pageId)
        .set({ readingStatus: "submitted", uploading: false }, { merge: true })
        .then(() => {
          db.collection("users")
            .doc(userId)
            .collection("submissions")
            .doc(bookId)
            .set({ timeSubmitted: new Date().getTime() }, { merge: true })
            .then(() => resolve())
            .catch(ex => {
              LogService.exception(
                ex,
                `FirebaseService.submitRecordingPage`,
                `Failed recording set. userId ${userId} bookId: ${bookId} pageId: ${pageId}`,
              );

              reject();
            });
        })
        .catch(ex => {
          LogService.exception(
            ex,
            `FirebaseService.submitRecordingPage`,
            `Failed recording set. userId ${userId} bookId: ${bookId} pageId: ${pageId}`,
          );

          reject();
        });
    });
  }

  static async deleteSubmission(userId: string, bookId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(bookId)
        .delete()
        .then(() => resolve())
        .catch(ex => {
          LogService.exception(ex, `FirebaseService.deleteSubmission`, `Failed deleting submission`);
          reject();
        });
    });
  }

  static async addComprehensionResponse(
    classId: string,
    studentId: string,
    commentId: string,
    assignmentId: string,
    response: {
      commentType: "oral" | "text";
      textComment?: string;
      audioPath?: string;
    },
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      db.collection("comprehension_messages")
        .doc(commentId + "_" + studentId)
        .set(
          {
            id: commentId,
            classId: classId,
            assignmentId: assignmentId,
            studentId: studentId,
            responses: firebase.firestore.FieldValue.arrayUnion({
              userId: studentId,
              timeStamp: new Date().getTime(),
              comment: response,
            }),
          },
          { merge: true },
        )
        .then(() => {
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  }

  static watchUserComment(userId: string, commentId: string, callback: (userData: ComprehensionMessage) => void) {
    return db
      .collection("comprehension_messages")
      .doc(commentId + "_" + userId)
      .onSnapshot(
        snap => {
          const data = snap.data();
          if (data) callback(data as ComprehensionMessage);
        },
        error => {
          LogService.exception(error, `FirebaseService.watchUserComment`);
        },
      );
  }

  static watchReadleeSettings(): () => void {
    return db.collection("settings").onSnapshot(snap => {
      snap.docs.forEach(document => {
        if (document.id === "dirty_definitions") {
          setDirtyWords((document.data() as { definitions: Array<string> }).definitions);
        } else if (document.id === "onboarding") {
          setOnboardingAssignments((document.data() as { assignments: Array<OnboardingAssignment> }).assignments);
          onboardingAssignments = (document.data() as { assignments: Array<OnboardingAssignment> }).assignments.map(
            a => a.assignmentId,
          );
        } else if (document.id === "demo_class") {
          setDemoAssignments((document.data() as { assignments: Array<string> }).assignments);
          demoAssignments = (document.data() as { assignments: Array<string> }).assignments;

          setDemoStudents((document.data() as { students: Array<string> }).students);
          demoStudents = (document.data() as { students: Array<string> }).students;

          setDemoClassId((document.data() as { demoClassId: string }).demoClassId);
          demoId = (document.data() as { demoClassId: string }).demoClassId;
        } else if (document.id === "admin") {
          setAdminId((document.data() as { id: string }).id);
        }
      });
    });
  }

  static async getRecordedPages(userId: string, submissionId: string): Promise<RecordedPage[]> {
    return (
      await db
        .collection("users")
        .doc(userId)
        .collection("submissions")
        .doc(submissionId)
        .collection("recorded_pages")
        .get()
    ).docs.map(doc => doc.data() as RecordedPage);
  }

  static watchOnboardingAssignments(callback: (words: Array<string>) => void): () => void {
    return db
      .collection("settings")
      .doc("dirty_definitions")
      .onSnapshot(snapshot => {
        const data = snapshot.data() as { definitions: Array<string> };
        callback(data.definitions);
      });
  }

  static setLastLoginDay(userId: string) {
    const lastLoginDay = new Date();

    try {
      db.collection("users")
        .doc(userId)
        .set(
          {
            lastLoginDay: lastLoginDay.toISOString(),
          },
          { merge: true },
        )
        .catch(err => {
          LogService.exception(err, `FirebaseService.setLastLoginDay`, "Failed to update last login day");
        });
    } catch (err) {
      LogService.exception(err, `FirebaseService.setLastLoginDay`, "Failed to update last login day");
    }
  }

  static getAssessment(assessmentId: string): Promise<Assessment> {
    return new Promise<Assessment>((resolve, reject) => {
      db.collection("assessments")
        .doc(assessmentId)
        .get()
        .then(snap => {
          const data = snap.data() as Assessment;
          resolve(data);
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  // Copy an assignment and its book and assign parentId to current user
  static async copyAssignment(
    original: Assignment,
    userDisplayName: string,
    newName?: string,
  ): Promise<{ id: string; bookId: string }> {
    const emptyResponse = { id: "", bookId: "" };
    try {
      const timestamp = new Date().toISOString();

      // Copy original assignment book into new book for new assignment
      const newBook = await this.copyBook(original.bookId);
      if (!newBook) {
        LogService.exception("Could not copy book", `FirebaseService.copyAssignment`);
        return emptyResponse;
      }
      // Set data for new assignment
      const res = db.collection("assignments").doc();
      await res.set({
        bookId: newBook,
        parentId: currentUserId,
        parent: userDisplayName,
        type: "teacher",
        id: res.id,
        title: newName || "Copy of " + original.title,
        isActive: true,
        studentFacingData: original.studentFacingData,
        assignmentComments: this.copyAssignmentQuestions(original.assignmentComments),
        dateCreated: timestamp,
        lastModified: timestamp,
        lastOpened: timestamp,
        color: { alpha: 255, red: 0, blue: 0, green: 0 },
      });

      const rootMyLibrary = (
        await db
          .collection("assignment_folders")
          .where("type", "==", "My Library")
          .where("parentId", "==", null)
          .where("ownerId", "==", currentUserId)
          .get()
      ).docs[0];

      await db
        .collection("assignment_folders")
        .doc(rootMyLibrary?.id)
        .set(
          {
            childAssignments: firebase.firestore.FieldValue.arrayUnion(res.id),
          },
          { merge: true },
        );

      return { id: res.id, bookId: newBook };
    } catch (error) {
      LogService.exception(error, `FirebaseService.copyAssignment`);
      return emptyResponse;
    }
  }

  static copyAssignmentQuestions(original: AssignmentComments | undefined): AssignmentComments {
    const empty: AssignmentComments = {
      replyType: "string",
    };

    try {
      if (original) {
        const result: AssignmentComments = {
          replyType: original.replyType,
          introAudio: original.introAudio,
          modelTextAudio: original.modelTextAudio,
          comprehensionQuestions: original.comprehensionQuestions?.map(question => {
            if (question.commentType === "oral") {
              const copy: ComprehensionQuestion = {
                id: uuid(),
                commentType: question.commentType,
                replyType: question.replyType,
                audioPath: question.audioPath,
              };
              return copy;
            }
            const copy: ComprehensionQuestion = {
              id: uuid(),
              commentType: question.commentType,
              replyType: question.replyType,
              textComment: question.textComment,
            };
            return copy;
          }),
        };

        return result;
      } else {
        return empty;
      }
    } catch (error) {
      LogService.exception(error, `FirebaseService.copyAssignmentQuestions`);
      return empty;
    }
  }

  // Copy entire book into new book in library, return new book Id
  static async copyBook(originalId: string): Promise<string | null> {
    try {
      // Fetch original book data
      const originalBook = (await db.collection("library").doc(originalId).get()).data() as Book;
      const pageId = originalBook.firstPage;
      const originalFirstPage = (
        await db.collection("library").doc(originalId).collection("pages").doc(pageId).get()
      ).data() as Page;
      let nextPageId = originalFirstPage.navigation.nextId;

      // Create book and first page
      const newBook = (await this.createNewBookAndPage(
        originalBook.name,
        originalBook.name,
        originalFirstPage.content,
        originalFirstPage.markupContent,
        originalFirstPage.fontFamily,
        null,
        null,
        null,
        null,
      )) as { book: string; page: string };

      // Store new book data
      const newBookId = newBook.book;
      let newPrevPageId = newBook.page;
      let prevPage;

      let currentPage;
      let count = 2;

      // Loop through original book pages and copy to new book
      while (nextPageId != null) {
        currentPage = (
          await db.collection("library").doc(originalId).collection("pages").doc(nextPageId).get()
        ).data() as Page;
        prevPage = await this.copyPage(newBookId, newPrevPageId, currentPage, count);
        newPrevPageId = prevPage as string;
        nextPageId = currentPage.navigation.nextId;
        count += 1;
      }

      return newBookId;
    } catch (error) {
      LogService.exception(error, `FirebaseService.copyBook`);
      return null;
    }
  }

  static async createAssignment(title: string, bookId: string) {
    try {
      const batch = db.batch();
      const assignmentRef = db.collection("assignments").doc();
      batch.set(assignmentRef, {
        title: title,
        bookId: bookId,
        id: assignmentRef.id,

        dateCreated: new Date().toISOString(),
        studentFacingData: {
          accuracy: true,
          completionPercentage: true,
          wordsPerMinute: true,
        },
      });
      await batch.commit();
      return assignmentRef.id;
    } catch (e) {
      return e;
    }
  }

  // Create a new book with one page and return the book and page Ids
  static async createNewBookAndPage(
    bookName: string,
    title: string,
    content: string,
    markupContent: string,
    fontFamily: string | null,
    pageContent: Array<string> | null,
    pdfPath: string | null,
    bookViewType: string | null,
    _eBookPath: string | null,
  ) {
    try {
      const batch = db.batch();

      // Create book and first page references
      const bookRef = db.collection("library").doc();
      const pageRef = db.collection("library").doc(bookRef.id).collection("pages").doc();

      // set book data
      batch.set(bookRef, {
        firstPage: pageRef.id,
        id: bookRef.id,
        name: bookName,
        private: currentUserId,
        timeCreated: Date.now(),
        pageContent: pageContent,
        pdfPath: pdfPath,
        viewType: bookViewType ?? "text",
      });

      // set first page data
      batch.set(pageRef, {
        content: content,
        markupContent: markupContent,
        title: title,
        fontFamily: fontFamily != null ? fontFamily : "Arial",
        navigation: {
          id: pageRef.id,
          nextId: null,
          pageNum: 1,
          previousId: null,
        },
      });

      await batch.commit();

      return {
        book: bookRef.id,
        page: pageRef.id,
      };
    } catch (error) {
      LogService.exception(error, `FirebaseService.createNewBookAndPage`);
    }

    return {
      book: "",
      page: "",
    };
  }

  // Copy a page into a new Page in bookId and return the new page Id
  static async copyPage(bookId: string, prevPageId: string, page: Page, count: number): Promise<string | null> {
    try {
      const batch = db.batch();

      // get previous page
      const prevRef = db.collection("library").doc(bookId).collection("pages").doc(prevPageId);

      // create new page
      const newRef = db.collection("library").doc(bookId).collection("pages").doc();

      // set previous page's next page id
      batch.set(prevRef, { navigation: { nextId: newRef.id } }, { merge: true });

      // set new page
      batch.set(newRef, {
        title: page.title,
        content: page.content,
        navigation: {
          pageNum: count,
          id: newRef.id,
          previousId: prevPageId,
          nextId: null,
        },
      });

      await batch.commit();

      return newRef.id;
    } catch (error) {
      LogService.exception(error, `FirebaseService.copyPage`);
      return null;
    }
  }

  // Send user password reset instructions
  static async sendPasswordResetEmail(email: string): Promise<string> {
    try {
      await auth.sendPasswordResetEmail(email, null);
      return "Email sent! Please check your inbox.";
    } catch (error) {
      LogService.exception(
        error,
        `FirebaseService.sendPasswordResetEmail`,
        "Something went wrong sending password reset email",
      );
      return error instanceof Error ? error.message : "Something went wrong, please try again!";
    }
  }

  // Update a classrooms student invite code in db
  static async updateClassCode(_currentClassCode: string, newClassCode: string, classID: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      // check if new class code exists
      const result = await db.collection("classes").where("classCode", "==", newClassCode).get();

      // reject if class code exists
      if (result != null && !result.empty) {
        reject(new Error("Class code is already taken."));
      } else {
        db.collection("classes")
          .doc(classID)
          .set({ classCode: newClassCode }, { merge: true })
          .then(() => resolve("Updated class code"))
          .catch(error => {
            LogService.exception(error, `FirebaseService.updateClassCode`);
            reject(new Error("Something went wrong."));
          });
      }
    });
  }

  // Delete classroom in db
  static async deleteClass(classId: string): Promise<void> {
    try {
      // get all class assignments
      const assignments = await db.collection("classes").doc(classId).collection("assignments").get();

      // run through and delete each class assignment doc
      for (let i = 0; i < assignments?.docs?.length ?? 0; i++) {
        await db.collection("classes").doc(classId).collection("assignments").doc(assignments.docs?.[i]?.id).delete();
      }

      // delete class doc
      await db.collection("classes").doc(classId).delete();
    } catch (error) {
      LogService.exception(error, `FirebaseService.deleteClass`);
    }
  }

  static async deleteAssignment(assignmentId: string): Promise<void> {
    try {
      const batch = db.batch();
      const [assignment, classes, shared, inbox] = await Promise.all([
        db.collection("assignments").doc(assignmentId),
        db.collection("classes").where("assignments", "array-contains", assignmentId).get(),
        db.collection("users").where("sharedAssignments", "array-contains", assignmentId).get(),
        db.collectionGroup("inbox").where("id", "==", assignmentId).get(),
      ]);

      classes.docs.forEach(doc => batch.update(doc.ref, { assignments: FieldValue.arrayRemove(assignmentId) }));
      shared.docs.forEach(doc => batch.update(doc.ref, { sharedAssignments: FieldValue.arrayRemove(assignmentId) }));
      inbox.docs.forEach(doc =>
        batch.delete(db.collection("users").doc(doc.data().recipientId).collection("inbox").doc(doc.id)),
      );
      batch.delete(assignment);
      await batch.commit();
    } catch (e) {
      throw e;
    }
  }

  // Delete classroom in db
  static async loginWithClever(
    code: string,
    redirect: string,
    success: () => void,
    onNewTeacher: (arg: Array<CleverClass>) => void,
    onNewStudent: (uid: string) => void,
  ): Promise<void> {
    const queryParams = new URLSearchParams(window.location.search);
    const stateParam = queryParams.get("state") || "";
    const classCode = stateParam.length > 0 ? JSON.parse(stateParam)["classCode"] : "";

    return new Promise<void>((resolve, reject) => {
      // fetch access token for user sign in
      const call = `https://us-central1-${
        process.env.REACT_APP_ENV === "production" ? "readtome-272220" : "readtomedev-81db7"
      }.cloudfunctions.net/cleverAuth?token=${code}&redirect=${redirect}`;

      axios
        .get(call)
        .then(result => {
          const data = result.data as {
            uid: string;
            token: string;
            userType: string;
            email: string;
            classes: Array<CleverClass>;
            newUser: boolean;
            name: string;
          };

          // if token is available (auth success) login with token
          if (data.token) {
            this.loginWithToken(data.token)
              .then(async () => {
                if (data.userType === "teacher" && data.newUser) {
                  this.createRootFolders(data.uid, []);
                  this.cloneDemoClass(data.name, data.email, data.uid);
                  onNewTeacher(data.classes);
                } else if (data.userType === "student" && data.newUser) {
                  if (classCode.length > 0) {
                    await this.addUserToClass(classCode, data.uid);
                  }
                  await this.addPreregisteredStudent(data.uid, data.email);
                  if (onNewStudent) onNewStudent(data.uid);
                  success();
                } else {
                  success();
                }
                resolve();
              })
              .catch(error => {
                LogService.exception(error, `FirebaseService.loginWithClever`);
                reject();
              });
          } else {
            LogService.exception(
              "missing token",
              `FirebaseService.loginWithClever`,
              "Clever Auth missing access token",
            );
            reject();
          }
        })
        .catch(error => {
          LogService.exception(error, `FirebaseService.loginWithClever`);
          reject();
        });
    });
  }

  // Create root folders for new user
  static async createRootFolders(userId: string, _classes: Array<Classroom>): Promise<void> {
    try {
      // create root folders
      await Promise.all([
        this.createNewAssignmentFolder(null, "My Library", "My Library", userId),
        this.createNewAssignmentFolder(null, "Trash", "Trash", userId),
        this.createNewAssignmentFolder(null, "Star", "Star", userId),
      ]);
    } catch (error) {
      LogService.exception(error, `FirebaseService.createRootFolders`);
    }
  }

  // Add / remove assignment from user's starred  folder. Return true on success
  static async updateAssignmentStarred(userId: string, assignmentId: string, isStarred: boolean): Promise<boolean> {
    try {
      if (userId.length === 0) return false;

      // Make sure star folder exists
      const folder = await db
        .collection("assignment_folders")
        .where("ownerId", "==", userId)
        .where("type", "==", "Star")
        .get();
      if (folder.empty) return false;
      if (!folder.docs[0]) return false;

      const starFolder = folder.docs[0].data() as AssignmentFolder;

      // Add/remove assignment
      await db
        .collection("assignment_folders")
        .doc(starFolder.id)
        .set(
          {
            childAssignments: isStarred
              ? firebase.firestore.FieldValue.arrayUnion(assignmentId)
              : firebase.firestore.FieldValue.arrayRemove(assignmentId),
          },
          { merge: true },
        );
      return true;
    } catch (error) {
      LogService.exception(error, `FirebaseService.updateAssignmentStarred`);
      return false;
    }
  }

  // For moving an assignment to/from trash, may redo with batching
  static async updateAssignmentIsActive(userId: string, assignmentId: string, active: boolean): Promise<boolean> {
    try {
      if (userId.length === 0) return false;
      const batch = db.batch();

      // set assignment to active/inactive
      batch.set(db.collection("assignments").doc(assignmentId), { isActive: active }, { merge: true });

      // Get user's root folders, classes with assignment, and users with assignment in shared
      const [folderQuery, classesQuery, sharedQuery] = await Promise.all([
        db.collection("assignment_folders").where("ownerId", "==", userId).get(),
        db.collection("classes").where("assignments", "array-contains", assignmentId).get(),
        db.collection("users").where("sharedAssignments", "array-contains", assignmentId).get(),
      ]);

      const folderMap: Map<string, AssignmentFolder> = new Map();

      folderQuery.docs.forEach(folder => {
        const data = folder.data() as AssignmentFolder;
        folderMap.set(data.type, data);
      });

      const TrashFolderId: string | undefined = folderMap.get("Trash")?.id;
      const MyLibraryFolderId: string | undefined = folderMap.get("My Library")?.id;
      const StarFolderId: string | undefined = folderMap.get("Star")?.id;

      // Ensure root folders exist
      if (!(TrashFolderId && MyLibraryFolderId && StarFolderId)) return false;

      // if moving from trash back to library
      if (active) {
        // Remove from trash folder
        batch.set(
          db.collection("assignment_folders").doc(TrashFolderId),
          {
            childAssignments: firebase.firestore.FieldValue.arrayRemove(assignmentId),
          },
          { merge: true },
        );

        // Add to library folder
        batch.set(
          db.collection("assignment_folders").doc(MyLibraryFolderId),
          {
            childAssignments: firebase.firestore.FieldValue.arrayUnion(assignmentId),
          },
          { merge: true },
        );
      } else {
        // Add to trash folder
        batch.set(
          db.collection("assignment_folders").doc(TrashFolderId),
          {
            childAssignments: firebase.firestore.FieldValue.arrayUnion(assignmentId),
          },
          { merge: true },
        );

        // Remove from library folder
        batch.set(
          db.collection("assignment_folders").doc(MyLibraryFolderId),
          {
            childAssignments: firebase.firestore.FieldValue.arrayRemove(assignmentId),
          },
          { merge: true },
        );

        // Remove from starred folder
        batch.set(
          db.collection("assignment_folders").doc(StarFolderId),
          {
            childAssignments: firebase.firestore.FieldValue.arrayRemove(assignmentId),
          },
          { merge: true },
        );

        // Remove assignment from each class where it was found
        let classId: string | undefined;
        for (let i = 0; i < classesQuery.docs.length; i++) {
          classId = classesQuery.docs[i]?.id;

          if (classId) {
            const classRef = db.collection("classes").doc(classId);
            const classAssignmentRef = db
              .collection("classes")
              .doc(classId)
              .collection("assignments")
              .doc(assignmentId);

            batch.update(classRef, {
              assignments: firebase.firestore.FieldValue.arrayRemove(classAssignmentRef.id),
            });
            batch.delete(classAssignmentRef);

            const rootFolder = await db
              .collection("assignment_folders")
              .where("parentId", "==", null)
              .where("type", "==", classId)
              .limit(1)
              .get();

            if (!rootFolder.empty) {
              const doc = rootFolder.docs[0];
              if (doc) {
                const rootFolderRef = db.collection("assignment_folders").doc(doc.id);
                batch.delete(rootFolderRef);
              }
            }
          }
        }

        sharedQuery.docs.forEach(doc =>
          batch.update(doc.ref, { sharedAssignments: FieldValue.arrayRemove(assignmentId) }),
        );
      }

      await batch.commit();
      return true;
    } catch (error) {
      LogService.exception(error, `FirebaseService.updateAssignmentIsActive`);
      return false;
    }
  }

  // Share assignment with another teacher
  static async shareAssignment(
    assignmentId: string,
    recipientEmail: string,
  ): Promise<{ result: boolean; msg: string }> {
    try {
      // find recipient via email
      const recipient = await db.collection("users").where("email", "==", recipientEmail).get();

      if (!recipient.docs[0]) return { result: false, msg: `Could not find user with email ${recipientEmail}` };
      if ((recipient.docs[0].data() as User).userType === "student")
        return { result: false, msg: `Cannot share assignment with student` };

      // Add to recipient's shared assignments list
      await db
        .collection("users")
        .doc(recipient.docs[0].id)
        .set(
          {
            sharedAssignments: firebase.firestore.FieldValue.arrayUnion(assignmentId),
          },
          { merge: true },
        );

      return { result: true, msg: `Assignment shared with ${recipientEmail}` };
    } catch (error) {
      LogService.exception(error, `FirebaseService.shareAssignment`);
      return { result: false, msg: "Could not share assignment" };
    }
  }
}

export default FirebaseService;
